import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  ArrowLeft,
  Trophy,
  Users,
  Calendar,
  Check,
  X,
  Plus,
  Loader2,
  Search,
  Grid,
  List,
  LayoutGrid,
  Filter,
  ChevronRight,
  Clock,
  Gamepad as Game,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  CardDescription,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import PullToRefresh from "react-simple-pull-to-refresh";
import { EnhancedHeader } from "@/components/ui";


// Define tournament schema for form validation
const createTournamentSchema = z.object({
  name: z.string().min(3, "Name must be at least 3 characters"),
  description: z.string().optional(),
  maxPlayers: z
    .number()
    .min(4, "Minimum 4 players required")
    .max(32, "Maximum 32 players allowed"),
  startDate: z.string().optional(),
  gameType: z.string().default("quick"),
});

type CreateTournamentForm = z.infer<typeof createTournamentSchema>;

type Tab = "active" | "pending" | "completed" | "invitations";

export default function TournamentsPage() {
  const [_, setLocation] = useLocation();
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [selectedTab, setSelectedTab] = useState<Tab>("active");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");

  // Form setup for tournament creation
  const form = useForm<CreateTournamentForm>({
    resolver: zodResolver(createTournamentSchema),
    defaultValues: {
      name: "",
      description: "",
      maxPlayers: 8,
      startDate: "",
      gameType: "regular",
    },
  });

  // Fetch tournaments data
  const { data: tournaments, isLoading: isLoadingTournaments } = useQuery({
    queryKey: ["tournaments"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/tournaments");
      if (!res.ok) throw new Error("Failed to fetch tournaments");
      return res.json();
    },
  });

  // Fetch tournament invitations
  const { data: invitations, isLoading: isLoadingInvitations } = useQuery({
    queryKey: ["tournament-invitations"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/tournaments/invitations");
      if (!res.ok) throw new Error("Failed to fetch tournament invitations");
      return res.json();
    },
  });

  // Calculate tournament statistics
  const tournamentStats = useMemo(() => {
    if (!tournaments) return { active: 0, upcoming: 0, completed: 0, participated: 0 };
    
    const active = tournaments.filter((t: any) => t.status === "active").length;
    const upcoming = tournaments.filter((t: any) => t.status === "pending").length;
    const completed = tournaments.filter((t: any) => t.status === "completed").length;
    
    // Count tournaments where the current user is a participant
    const participated = tournaments.filter((t: any) => {
      try {
        const participants = Array.isArray(t.participants) 
          ? t.participants 
          : JSON.parse(t.participants || "[]");
        return participants.includes(user?.id);
      } catch {
        return false;
      }
    }).length;
    
    return { active, upcoming, completed, participated };
  }, [tournaments, user?.id]);

  // Create tournament mutation
  const createTournamentMutation = useMutation({
    mutationFn: async (data: CreateTournamentForm) => {
      const res = await apiRequest("POST", "/api/tournaments", data);
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to create tournament");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Tournament created successfully",
        duration: 3000,
      });
      setCreateDialogOpen(false);
      form.reset();
      queryClient.invalidateQueries({ queryKey: ["tournaments"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Response to invitation mutation
  const respondToInvitationMutation = useMutation({
    mutationFn: async ({
      id,
      response,
    }: {
      id: number;
      response: "accept" | "decline";
    }) => {
      const res = await apiRequest(
        "PUT",
        `/api/tournaments/invitations/${id}`,
        { response },
      );
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to respond to invitation");
      }
      return res.json();
    },
    onSuccess: (_, variables) => {
      toast({
        title: "Success",
        description: `Invitation ${variables.response === "accept" ? "accepted" : "declined"} successfully`,
        duration: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ["tournament-invitations"] });
      queryClient.invalidateQueries({ queryKey: ["tournaments"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Handle tournament creation
  const onSubmit = (data: CreateTournamentForm) => {
    createTournamentMutation.mutate(data);
  };

  // Filter tournaments based on status and search term
  const filteredTournaments = tournaments?.filter((tournament: any) => {
    const matchesTab =
      (selectedTab === "active" && tournament.status === "active") ||
      (selectedTab === "pending" && tournament.status === "pending") ||
      (selectedTab === "completed" && tournament.status === "completed");

    const matchesSearch =
      tournament.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tournament.description &&
        tournament.description
          .toLowerCase()
          .includes(searchTerm.toLowerCase()));

    return matchesTab && matchesSearch;
  });

  const handleRefresh = async () => {
    // Refresh all relevant queries based on the current tab
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["tournaments"] }),
      queryClient.invalidateQueries({ queryKey: ["tournament-invitations"] })
    ]);

    toast({
      title: "Refreshed",
      description: "Tournament data has been updated",
      duration: 2000,
    });
    
    return Promise.resolve();
  };

  return (
      <div className="container mx-auto px-4 pb-20">
        
        <EnhancedHeader
          title="Tournaments"
          backPath="/games"
          coloredText={true}
          rightContent={<Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={() => setCreateDialogOpen(true)}
          >
            <Plus className="h-4 w-4" />
            <span className="font-medium">Create</span>
          </Button>}
          showBackButton={true}
        />

        <PullToRefresh 
          onRefresh={handleRefresh}
          className="min-h-screen"
          pullingContent={
            <div className="flex justify-center pt-4 pb-3 theme-text-muted">
              <Loader2 className="animate-spin h-5 w-5 mr-2" /> Pull to refresh...
            </div>
          }
          refreshingContent={
            <div className="flex justify-center pt-4 pb-3 theme-text-primary">
              <Loader2 className="animate-spin h-5 w-5 mr-2" /> Refreshing...
            </div>
          }
        >
          <div>

        
        {/* Tournament Statistics Overview */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Card className="theme-bg-secondary/10 border-none shadow-sm">
            <CardContent className="p-3 flex items-center justify-between">
              <div>
                <p className="text-xs theme-text-muted">Active</p>
                <p className="text-xl font-semibold theme-text-primary">{tournamentStats.active}</p>
              </div>
              <div className="p-2 rounded-full theme-bg-primary/20">
                <Trophy className="h-5 w-5 theme-text-primary" />
              </div>
            </CardContent>
          </Card>
          <Card className="theme-bg-secondary/10 border-none shadow-sm">
            <CardContent className="p-3 flex items-center justify-between">
              <div>
                <p className="text-xs theme-text-muted">Upcoming</p>
                <p className="text-xl font-semibold theme-text-primary">{tournamentStats.upcoming}</p>
              </div>
              <div className="p-2 rounded-full theme-bg-primary/20">
                <Calendar className="h-5 w-5 theme-text-primary" />
              </div>
            </CardContent>
          </Card>
          <Card className="theme-bg-secondary/10 border-none shadow-sm">
            <CardContent className="p-3 flex items-center justify-between">
              <div>
                <p className="text-xs theme-text-muted">Completed</p>
                <p className="text-xl font-semibold theme-text-primary">{tournamentStats.completed}</p>
              </div>
              <div className="p-2 rounded-full theme-bg-primary/20">
                <Check className="h-5 w-5 theme-text-primary" />
              </div>
            </CardContent>
          </Card>
          <Card className="theme-bg-secondary/10 border-none shadow-sm">
            <CardContent className="p-3 flex items-center justify-between">
              <div>
                <p className="text-xs theme-text-muted">Participated</p>
                <p className="text-xl font-semibold theme-text-primary">{tournamentStats.participated}</p>
              </div>
              <div className="p-2 rounded-full theme-bg-primary/20">
                <Users className="h-5 w-5 theme-text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Modern Tabs Using Shadcn UI Tabs Component */}
        <Tabs 
          defaultValue={selectedTab} 
          onValueChange={(value) => setSelectedTab(value as Tab)}
          className="mb-6"
        >
          <TabsList className="grid w-full grid-cols-4 h-11 theme-bg-secondary/20 p-1 rounded-xl">
            <TabsTrigger 
              value="active" 
              className="rounded-lg data-[state=active]:theme-bg-primary data-[state=active]:text-white"
            >
              Active
              {tournamentStats.active > 0 && (
                <span className="ml-1 text-xs theme-text-primary data-[state=active]:text-white">
                  ({tournamentStats.active})
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="pending" 
              className="rounded-lg data-[state=active]:theme-bg-primary data-[state=active]:text-white"
            >
              Upcoming
              {tournamentStats.upcoming > 0 && (
                <span className="ml-1 text-xs theme-text-primary data-[state=active]:text-white">
                  ({tournamentStats.upcoming})
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="completed" 
              className="rounded-lg data-[state=active]:theme-bg-primary data-[state=active]:text-white"
            >
              Past
            </TabsTrigger>
            <TabsTrigger 
              value="invitations" 
              className="rounded-lg data-[state=active]:theme-bg-primary data-[state=active]:text-white"
            >
              Invites
              {invitations?.filter((inv: any) => inv.status === "pending").length > 0 && (
                <Badge className="ml-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs">
                  {invitations.filter((inv: any) => inv.status === "pending").length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Search Bar with View Mode Toggle */}
        <div className="flex justify-between items-center gap-3 mb-6">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 theme-text-muted" />
            </div>
            <Input
              placeholder="Search tournaments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-3 theme-bg-secondary/30 theme-border-transparent rounded-xl theme-text placeholder:theme-text-muted"
            />
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex rounded-lg overflow-hidden border theme-border">
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-10 w-10 rounded-none ${viewMode === 'list' ? 'theme-bg-primary theme-text-contrast' : ''}`}
                    onClick={() => setViewMode('list')}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-10 w-10 rounded-none ${viewMode === 'grid' ? 'theme-bg-primary theme-text-contrast' : ''}`}
                    onClick={() => setViewMode('grid')}
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Change view</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Main Content */}
        <AnimatePresence mode="wait">
          {selectedTab !== "invitations" ? (
            <motion.div
              key="tournaments"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {isLoadingTournaments ? (
                <div className="flex justify-center items-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin theme-text-primary" />
                </div>
              ) : filteredTournaments?.length > 0 ? (
                <div className={`${viewMode === 'grid' ? 'grid grid-cols-2 gap-3' : 'space-y-4'}`}>
                  {filteredTournaments.map((tournament: any) => (
                    <motion.div
                      key={tournament.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Card
                        className={`theme-card overflow-hidden ${viewMode === 'grid' ? 'h-full flex flex-col' : ''}`}
                      >
                        <CardHeader className={`${viewMode === 'grid' ? 'p-3 pb-2' : 'pb-3'}`}>
                          <div className="space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <div className="rounded-full p-1 theme-bg-primary/20 flex items-center justify-center flex-shrink-0">
                                  <Trophy className="h-3.5 w-3.5 theme-text-primary" />
                                </div>
                                <CardTitle className={`${viewMode === 'grid' ? 'text-sm' : 'text-base'} font-semibold truncate min-w-0`}>
                                  {tournament.name}
                                </CardTitle>
                              </div>
                              <Badge
                                variant="outline"
                                className={`flex-shrink-0 text-xs px-2 py-0.5 ${
                                  tournament.status === "active"
                                    ? "theme-bg-primary theme-text-contrast border-transparent"
                                    : tournament.status === "pending"
                                      ? "bg-yellow-500/10 text-yellow-600 border-yellow-200"
                                      : "bg-gray-500/10 text-gray-600 border-gray-200"
                                }`}
                              >
                                {tournament.status === "active"
                                  ? "Active"
                                  : tournament.status === "pending"
                                    ? "Upcoming"
                                    : "Completed"}
                              </Badge>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className={`${viewMode === 'grid' ? 'p-3 pt-0 pb-2 flex-1' : 'pb-4'}`}>
                          {tournament.description && (
                            <p className={`text-xs mb-3 theme-text-muted ${viewMode === 'grid' ? 'line-clamp-2' : ''}`}>
                              {tournament.description}
                            </p>
                          )}
                          
                          <div className={`${viewMode === 'grid' ? 'space-y-2' : 'flex flex-wrap gap-4'} text-xs`}>
                            <div className="flex items-center gap-1.5">
                              <Users className="h-3.5 w-3.5 theme-text-primary flex-shrink-0" />
                              <span className="text-xs font-medium">
                                {(() => {
                                  function safelyParseParticipants(
                                    participants: any,
                                  ): number[] {
                                    try {
                                      if (Array.isArray(participants)) {
                                        return participants;
                                      }
                                      if (typeof participants === "string") {
                                        const parsed = JSON.parse(participants || "[]");
                                        return Array.isArray(parsed) ? parsed : [];
                                      }
                                      return [];
                                    } catch (error) {
                                      return [];
                                    }
                                  }
                                  const participants = safelyParseParticipants(tournament.participants);
                                  return participants.length;
                                })()}
                                /{tournament.maxPlayers} Players
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Calendar className="h-3.5 w-3.5 theme-text-primary flex-shrink-0" />
                              <span className="text-xs font-medium">
                                {tournament.startDate
                                  ? new Date(tournament.startDate).toLocaleDateString()
                                  : "Not scheduled"}
                              </span>
                            </div>
                            
                            {tournament.gameType && (
                              <div className="flex items-center gap-1.5">
                                <Game className="h-3.5 w-3.5 theme-text-primary flex-shrink-0" />
                                <span className="text-xs font-medium capitalize">{tournament.gameType}</span>
                              </div>
                            )}
                          </div>
                          
                          {tournament.status === "active" && (
                            <div className="mt-3 text-xs theme-text-primary font-medium flex items-center gap-1">
                              <Clock className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">
                                {tournament.nextRoundTime 
                                  ? `Next round: ${new Date(tournament.nextRoundTime).toLocaleString()}`
                                  : "Matches in progress"}
                              </span>
                            </div>
                          )}
                        </CardContent>
                        
                        {viewMode === "list" && <Separator />}
                        
                        <CardFooter className={`${viewMode === 'grid' ? 'p-3 pt-0' : 'pt-4'} justify-end`}>
                          <Button
                            onClick={() => setLocation(`/tournament/${tournament.id}`)}
                            variant="default"
                            size={viewMode === "grid" ? "sm" : "default"}
                            className="font-medium w-full justify-center text-xs"
                          >
                            {viewMode === "grid" ? (
                              <span className="font-medium">View</span>
                            ) : (
                              <span className="font-medium">
                                {tournament.status === "active"
                                  ? "View Matches"
                                  : tournament.status === "pending"
                                    ? "View Details"
                                    : "View Results"}
                              </span>
                            )}
                            <ChevronRight className="h-3.5 w-3.5 ml-1" />
                          </Button>
                        </CardFooter>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="text-center py-16 theme-border border-dashed rounded-xl border"
                >
                  <div className="rounded-full bg-primary/10 p-4 w-20 h-20 mx-auto mb-5 flex items-center justify-center">
                    <Trophy className="h-10 w-10 theme-text-primary opacity-80" />
                  </div>
                  <h3 className="text-xl font-medium theme-text mb-1">
                    No tournaments found
                  </h3>
                  <p className="text-sm theme-text-muted mb-4 max-w-sm mx-auto">
                    {selectedTab === "active"
                      ? "There are no active tournaments right now. Create one or switch to another category."
                      : selectedTab === "pending"
                        ? "There are no upcoming tournaments scheduled. Why not create a new one?"
                        : "You haven't participated in any completed tournaments yet."}
                  </p>
                  <Button
                    onClick={() => setCreateDialogOpen(true)}
                    variant="default"
                    className="mt-2 font-medium"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    <span className="font-medium">Create Tournament</span>
                  </Button>
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="invitations"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="space-y-4"
            >
              {isLoadingInvitations ? (
                <div className="flex justify-center items-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin theme-text-primary" />
                </div>
              ) : invitations?.length > 0 ? (
                // Sort invitations - pending first, then most recent by update date
                [...invitations]
                  .sort((a, b) => {
                    // Pending invitations at the top
                    if (a.status === "pending" && b.status !== "pending")
                      return -1;
                    if (a.status !== "pending" && b.status === "pending")
                      return 1;

                    // Then sort by most recent update/creation date
                    const aDate = new Date(a.updatedAt || a.createdAt).getTime();
                    const bDate = new Date(b.updatedAt || b.createdAt).getTime();
                    return bDate - aDate; // newest first
                  })
                  .map((invitation: any) => (
                    <motion.div
                      key={invitation.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Card
                        key={invitation.id}
                        className={`theme-card overflow-hidden border-l-4 ${
                          invitation.status === "pending"
                            ? "border-l-yellow-500"
                            : invitation.status === "accepted"
                              ? "border-l-green-500"
                              : invitation.status === "declined"
                                ? "border-l-red-500" 
                                : "border-l-gray-400"
                        }`}
                      >
                        <CardHeader className={`pb-2 ${invitation.status === "pending" ? "bg-yellow-50/30 dark:bg-yellow-950/10" : ""}`}>
                          <div className="flex justify-between items-center">
                            <CardTitle className="text-lg flex items-center gap-2">
                              <div className="rounded-full p-1 theme-bg-primary/20 flex items-center justify-center">
                                <Trophy className="h-5 w-5 theme-text-primary" />
                              </div>
                              <span className="truncate">{invitation.tournamentName || "Tournament Invitation"}</span>
                            </CardTitle>
                            <Badge
                              variant="outline"
                              className={`${
                                invitation.status === "pending"
                                  ? "bg-yellow-500/30 text-yellow-700 dark:text-yellow-300 border-yellow-400"
                                  : invitation.status === "accepted"
                                    ? "bg-green-500/30 text-green-700 dark:text-green-300 border-green-400"
                                    : invitation.status === "declined"
                                      ? "bg-red-500/30 text-red-700 dark:text-red-300 border-red-400"
                                      : "bg-gray-500/30 text-gray-700 dark:text-gray-300 border-gray-400"
                              }`}
                            >
                              {invitation.status === "pending"
                                ? "Pending"
                                : invitation.status === "accepted"
                                  ? "Accepted"
                                  : invitation.status === "declined"
                                    ? "Declined"
                                    : invitation.status.charAt(0).toUpperCase() +
                                      invitation.status.slice(1)}
                            </Badge>
                          </div>
                          {invitation.status === "pending" && (
                            <CardDescription className="mt-1 text-amber-600 dark:text-amber-400 flex items-center gap-1 font-medium">
                              <Clock className="h-3 w-3" />
                              Awaiting your response
                            </CardDescription>
                          )}
                        </CardHeader>
                        <CardContent className="pb-2">
                          <div className="mb-3">
                            <p className="text-sm theme-text">
                              {invitation.isJoinRequest
                                ? "You requested to join this tournament"
                                : `You've been invited to join this tournament by ${invitation.inviterName || "a host"}.`}
                            </p>
                            <div className="flex justify-between items-center mt-1">
                              <div className="text-xs theme-text-muted">
                                {invitation.status === "pending"
                                  ? `Received ${new Date(invitation.createdAt).toLocaleDateString()}`
                                  : invitation.status === "accepted"
                                    ? `Accepted ${new Date(invitation.updatedAt || invitation.createdAt).toLocaleDateString()}`
                                    : invitation.status === "declined"
                                      ? `Declined ${new Date(invitation.updatedAt || invitation.createdAt).toLocaleDateString()}`
                                      : `Updated ${new Date(invitation.updatedAt || invitation.createdAt).toLocaleDateString()}`}
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mb-2">
                            <div className="flex flex-col">
                              <span className="text-xs mb-1 theme-text-muted">
                                Participants
                              </span>
                              <div className="flex items-center gap-1">
                                <Users className="h-4 w-4 theme-text-primary" />
                                <span className="text-sm font-medium theme-text">
                                  {invitation.playerCount || "0"}/
                                  {invitation.maxPlayers || "8"} Players
                                </span>
                              </div>
                            </div>

                            <div className="flex flex-col">
                              <span className="text-xs mb-1 theme-text-muted">
                                Start Date
                              </span>
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4 theme-text-primary" />
                                <span className="text-sm font-medium theme-text">
                                  {invitation.startDate
                                    ? new Date(
                                        invitation.startDate,
                                      ).toLocaleDateString()
                                    : "Not scheduled"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                        {invitation.status === "pending" && (
                          <>
                            <Separator />
                            <CardFooter className="pt-4 flex justify-end gap-3">
                              <Button
                                variant="outline"
                                onClick={() =>
                                  respondToInvitationMutation.mutate({
                                    id: invitation.id,
                                    response: "decline",
                                  })
                                }
                                className="flex-1 flex items-center justify-center gap-2 bg-red-100 dark:bg-red-950/30 text-red-700 dark:text-red-400 border-red-200 hover:bg-red-200 dark:hover:bg-red-950/50"
                              >
                                <X className="h-4 w-4" />
                                <span className="font-medium">Decline</span>
                              </Button>
                              <Button
                                onClick={() =>
                                  respondToInvitationMutation.mutate({
                                    id: invitation.id,
                                    response: "accept",
                                  })
                                }
                                variant="default"
                                className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white hover:bg-green-700"
                              >
                                <Check className="h-4 w-4" />
                                <span className="font-medium">Accept</span>
                              </Button>
                            </CardFooter>
                          </>
                        )}
                        {invitation.status === "accepted" && (
                          <>
                            <Separator />
                            <CardFooter className="pt-3">
                              <Button
                                onClick={() => setLocation(`/tournament/${invitation.tournamentId}`)}
                                variant="default"
                                className="w-full justify-center"
                              >
                                View Tournament
                                <ChevronRight className="h-4 w-4 ml-1" />
                              </Button>
                            </CardFooter>
                          </>
                        )}
                      </Card>
                    </motion.div>
                  ))
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="text-center py-16 theme-border border-dashed rounded-xl border"
                >
                  <div className="rounded-full bg-primary/10 p-4 w-20 h-20 mx-auto mb-5 flex items-center justify-center">
                    <Trophy className="h-10 w-10 theme-text-primary opacity-80" />
                  </div>
                  <h3 className="text-xl font-medium theme-text mb-1">
                    No tournament invitations
                  </h3>
                  <p className="text-sm theme-text-muted mb-4 max-w-sm mx-auto">
                    You haven't received or requested to join any tournaments yet.
                    Join active tournaments or wait for invitations.
                  </p>
                  <Button
                    onClick={() => setSelectedTab("active")}
                    variant="outline"
                    className="mt-2 font-medium mr-2"
                  >
                    <Trophy className="h-4 w-4 mr-2" />
                    <span className="font-medium">Browse Tournaments</span>
                  </Button>
                  <Button
                    onClick={() => setCreateDialogOpen(true)}
                    variant="default"
                    className="mt-2 font-medium"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    <span className="font-medium">Create Tournament</span>
                  </Button>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Create Tournament Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-[500px] theme-bg-card">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <Trophy className="h-5 w-5 theme-text-primary" />
                Create New Tournament
              </DialogTitle>
              <DialogDescription>
                Fill out the form below to create a new tournament. All players will need to join before the tournament can begin.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <ScrollArea className="max-h-[60vh]">
                <div className="grid gap-4 py-4 px-1">
                  <div className="grid gap-2">
                    <label
                      htmlFor="name"
                      className="text-sm font-medium theme-text"
                    >
                      Tournament Name*
                    </label>
                    <Input
                      id="name"
                      placeholder="Enter tournament name"
                      className="theme-bg-secondary/20 theme-border rounded-lg"
                      {...form.register("name")}
                    />
                    {form.formState.errors.name && (
                      <p className="text-xs text-red-500">
                        {form.formState.errors.name.message}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <label
                      htmlFor="description"
                      className="text-sm font-medium theme-text flex items-center justify-between"
                    >
                      <span>Description</span>
                      <span className="text-xs theme-text-muted">(Optional)</span>
                    </label>
                    <Input
                      id="description"
                      placeholder="Enter tournament description"
                      className="theme-bg-secondary/20 theme-border rounded-lg"
                      {...form.register("description")}
                    />
                  </div>

                  <div className="grid gap-2">
                    <label
                      htmlFor="maxPlayers"
                      className="text-sm font-medium theme-text"
                    >
                      Maximum Players*
                    </label>
                    <Controller
                      name="maxPlayers"
                      control={form.control}
                      render={({ field }) => (
                        <Select
                          value={field.value.toString()}
                          onValueChange={(value) => field.onChange(parseInt(value))}
                        >
                          <SelectTrigger className="theme-bg-secondary/20 theme-border rounded-lg">
                            <SelectValue placeholder="Select number of players" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="4">4 Players</SelectItem>
                            <SelectItem value="8">8 Players</SelectItem>
                            <SelectItem value="16">16 Players</SelectItem>
                            <SelectItem value="32">32 Players</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {form.formState.errors.maxPlayers && (
                      <p className="text-xs text-red-500">
                        {form.formState.errors.maxPlayers.message}
                      </p>
                    )}
                    <p className="text-xs theme-text-muted mt-1">
                      Tournament brackets are automatically created based on player count.
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <label
                      htmlFor="startDate"
                      className="text-sm font-medium theme-text flex items-center justify-between"
                    >
                      <span>Start Date</span>
                      <span className="text-xs theme-text-muted">(Optional)</span>
                    </label>
                    <Input
                      type="date"
                      id="startDate"
                      className="theme-bg-secondary/20 theme-border rounded-lg"
                      {...form.register("startDate")}
                    />
                    <p className="text-xs theme-text-muted mt-1">
                      If no date is set, the tournament will start when all slots are filled.
                    </p>
                  </div>
                  
                  <div className="grid gap-2">
                    <label
                      htmlFor="gameType"
                      className="text-sm font-medium theme-text"
                    >
                      Game Type
                    </label>
                    <Controller
                      name="gameType"
                      control={form.control}
                      render={({ field }) => (
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger className="theme-bg-secondary/20 theme-border rounded-lg">
                            <SelectValue placeholder="Select game type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="tournament">Tournament</SelectItem>
                            <SelectItem value="quick">Quick</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>
              </ScrollArea>
              <DialogFooter className="pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCreateDialogOpen(false)}
                  className="bg-background text-foreground hover:bg-accent"
                >
                  <X className="h-4 w-4 mr-2" />
                  <span className="font-medium">Cancel</span>
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  disabled={createTournamentMutation.isPending}
                  className="font-medium"
                >
                  {createTournamentMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <span className="font-medium">Creating...</span>
                    </>
                  ) : (
                    <>
                      <Trophy className="h-4 w-4 mr-2" />
                      <span className="font-medium">Create Tournament</span>
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>

        </PullToRefresh>
      </div>
   
  );
}
